/**
 * 界面偏好登记表（内容宽度 / 动效 / 圆角 / 表格密度）——与 `borders.ts` 同一模式，只存"参数"，
 * 取值与下发一律由 `applyTheme.ts` 完成（唯一下发点）。
 *
 * 为什么要这几组：都属于「两种合理偏好并存、不该替所有人定死」的开关——
 * - **内容宽度**：宽表要通栏，阅读型页面在 2K/4K 上通栏则行长过长、KPI 卡被拉散；
 * - **动效**：数字滚动与"进行中"脉冲对多数人是实时感，对少数人是干扰源（也是 WCAG 2.3.3 的关切）；
 * - **圆角**：各主题自带 10/12/14 的圆角是主题身份，但有人整体偏好更硬朗或更圆润的界面语言；
 * - **表格密度**：一屏行数是作业诉求（核对名单/翻呼叫记录），但改字号会破坏列宽与截断口径，故只调行高。
 * 各档默认值都等于"改动前的既有观感"：改设置不该无故改观感，只有用户显式选择才变。
 */

export type WidthLevelKey = 'full' | 'w1440' | 'w1280';
export type MotionLevelKey = 'standard' | 'reduced' | 'system';
export type RadiusLevelKey = 'theme' | 'sharp' | 'standard' | 'round';
export type DensityLevelKey = 'loose' | 'standard' | 'compact';
export type LandingLevelKey = 'dashboard' | 'workbench' | 'inbox';

export interface WidthLevelDef {
  key: WidthLevelKey;
  name: string;
  desc: string;
  /** 内容区最大宽度（`none` = 通栏） */
  max: string;
}

export interface MotionLevelDef {
  key: MotionLevelKey;
  name: string;
  desc: string;
}

export interface RadiusLevelDef {
  key: RadiusLevelKey;
  name: string;
  desc: string;
  /** 卡片/弹层圆角；`theme` 档取主题自带的 radiusCard */
  card?: number;
  /** 按钮/输入框/菜单圆角（= card - 2 的既有口径） */
  control?: number;
  /** 胶囊（状态标签）圆角：22px 高配 11 = 全圆 */
  pill?: number;
  /** 选择器/分页/提示圆角（既有编译期档） */
  small?: number;
}

export const WIDTH_LEVELS: WidthLevelDef[] = [
  { key: 'full', name: '通栏', desc: '内容铺满可用宽度（宽表不折行、不横向滚动）', max: 'none' },
  { key: 'w1440', name: '1440 居中', desc: '超宽屏上行长更舒适，宽表仍基本可用', max: '1440px' },
  { key: 'w1280', name: '1280 居中', desc: '阅读型页面更聚焦；列很多的表会出现横向滚动', max: '1280px' },
];

export const MOTION_LEVELS: MotionLevelDef[] = [
  { key: 'standard', name: '标准', desc: '保留数字滚动、页面淡入与"进行中"脉冲呼吸' },
  { key: 'reduced', name: '减弱', desc: '关掉循环动画与数字翻牌，仅保留加载指示' },
  { key: 'system', name: '跟随系统', desc: '按操作系统的"减少动态效果"设置自动切换' },
];

export const RADIUS_LEVELS: RadiusLevelDef[] = [
  { key: 'theme', name: '跟随主题', desc: '用各主题自带的圆角（10 / 12 / 14）' },
  { key: 'sharp', name: '直角', desc: '卡片 4 · 按钮输入 2 · 胶囊 3', card: 4, control: 2, pill: 3, small: 4 },
  { key: 'standard', name: '标准', desc: '卡片 10 · 按钮输入 8 · 胶囊 11', card: 10, control: 8, pill: 11, small: 6 },
  { key: 'round', name: '圆润', desc: '卡片 14 · 按钮输入 12 · 胶囊 11', card: 14, control: 12, pill: 11, small: 10 },
];

export const DEFAULT_WIDTH: WidthLevelKey = 'full';
export const DEFAULT_MOTION: MotionLevelKey = 'standard';
export const DEFAULT_RADIUS: RadiusLevelKey = 'theme';
export const DEFAULT_DENSITY: DensityLevelKey = 'standard';
export const DEFAULT_LANDING: LandingLevelKey = 'dashboard';
export const DEFAULT_MENU_EXPAND: MenuExpandLevelKey = 'current';
export const DEFAULT_TIME_FMT: TimeFmtLevelKey = 'absolute';

/**
 * 表格密度档：只调「单元格纵向内边距」一个量，不动字号与列宽——
 * 一屏能看多少行是高频诉求（核对名单、翻 CDR），但改字号会连带破坏列宽基线与 ellipsis 截断口径。
 * 默认档 = 改动前的观感（Arco size=default 的 9px），故默认不写任何行内值。
 */
export interface DensityLevelDef {
  key: DensityLevelKey;
  name: string;
  desc: string;
  /** 单元格纵向内边距（px）；省略 = 不覆盖，回落 Arco 默认 9px */
  cellY?: number;
}

export const DENSITY_LEVELS: DensityLevelDef[] = [
  { key: 'loose', name: '宽松', desc: '单元格上下留白更多，长表格更好点选', cellY: 13 },
  { key: 'standard', name: '标准', desc: '与改动前一致的默认密度' },
  { key: 'compact', name: '紧凑', desc: '一屏多看 3~4 行，适合核对名单/翻呼叫记录', cellY: 5 },
];

/**
 * 登录落点档：登录成功后的第一屏。
 * 坐席几乎整天待在工作台、班长从待办中心开始，统一落在仪表盘等于每天都多一次跳转。
 */
export interface LandingLevelDef {
  key: LandingLevelKey;
  name: string;
  desc: string;
  /** 登录后的跳转路径（无 401 回跳目标时生效） */
  path: string;
}

export const LANDING_LEVELS: LandingLevelDef[] = [
  { key: 'dashboard', name: '仪表盘', desc: '先看经营与作业概览（默认）', path: '/dashboard' },
  { key: 'workbench', name: '坐席工作台', desc: '上班即待命：直接进入软电话与接待界面', path: '/workbench' },
  { key: 'inbox', name: '待办中心', desc: '先清「等我处理」的待办（审批环节/知识库待审）', path: '/workbench/tasks' },
];

export type MenuExpandLevelKey = 'current' | 'all' | 'pinned';
export type TimeFmtLevelKey = 'absolute' | 'relative';

/**
 * 侧栏分组默认展开档：决定**进来时**侧栏展开哪些一级分组（此后的手动收展照旧按用户意图记忆）。
 * 默认 `current` 即既有行为——20 组全展开会把菜单撑到数屏；但常驻某几个分组的人每次都
 * 要先点开同一组，故给出「常开固定分组」这一档（`pinned` 的组键存在 `bcc.menu.pinned`）。
 */
export interface MenuExpandLevelDef {
  key: MenuExpandLevelKey;
  name: string;
  desc: string;
}

/**
 * 时间显示档：列表时间列是看「绝对时刻」（对单查证、和客户对时间）还是看「距今多久」
 * （刚发生的事一眼看出新旧）。两档都只影响**展示**，导出与接口一律绝对值（见 utils/format 的
 * `fmtTime` / `fmtTimeRel` 双出口），另一形态在悬浮提示里给出。
 */
export interface TimeFmtLevelDef {
  key: TimeFmtLevelKey;
  name: string;
  desc: string;
}

export const TIME_FMT_LEVELS: TimeFmtLevelDef[] = [
  { key: 'absolute', name: '绝对时间', desc: '如 2026-03-04 14:30:00；悬浮提示距今多久（默认）' },
  { key: 'relative', name: '相对时间', desc: '如「12 分钟前」「昨天 14:30」；悬浮提示精确时刻' },
];

export const MENU_EXPAND_LEVELS: MenuExpandLevelDef[] = [
  { key: 'current', name: '跟随当前页', desc: '只展开当前页所在分组，其余按你的手动收展记忆（默认）' },
  { key: 'pinned', name: '常开分组', desc: '始终展开你勾选的分组（含当前页所在分组）' },
  { key: 'all', name: '全部展开', desc: '所有分组一次铺开；菜单会变长，需要上下滚动' },
];

/** 按 key 取档位（未知 key 回退默认档；不要写成固定下标，否则改默认值时存储空值会落到另一档） */
function pick<T extends { key: string }>(list: T[], key: string | null | undefined, fallback: string): T {
  return list.find((x) => x.key === key) ?? list.find((x) => x.key === fallback) ?? list[0];
}

export const getWidthLevel = (key: string | null | undefined) => pick(WIDTH_LEVELS, key, DEFAULT_WIDTH);
export const getMotionLevel = (key: string | null | undefined) => pick(MOTION_LEVELS, key, DEFAULT_MOTION);
export const getRadiusLevel = (key: string | null | undefined) => pick(RADIUS_LEVELS, key, DEFAULT_RADIUS);
export const getDensityLevel = (key: string | null | undefined) => pick(DENSITY_LEVELS, key, DEFAULT_DENSITY);
export const getLandingLevel = (key: string | null | undefined) => pick(LANDING_LEVELS, key, DEFAULT_LANDING);
export const getMenuExpandLevel = (key: string | null | undefined) => pick(MENU_EXPAND_LEVELS, key, DEFAULT_MENU_EXPAND);
export const getTimeFmtLevel = (key: string | null | undefined) => pick(TIME_FMT_LEVELS, key, DEFAULT_TIME_FMT);

/** 圆角档位 → 实际四档圆角值（`theme` 档用主题自带 radiusCard） */
export function resolveRadius(level: RadiusLevelDef, themeRadiusCard: number) {
  if (level.key === 'theme') return { card: themeRadiusCard, control: themeRadiusCard - 2, pill: 11, small: 6 };
  return { card: level.card!, control: level.control!, pill: level.pill!, small: level.small! };
}

/** 动效档位 → 实际是否减弱（`system` 由 `applyTheme` 依操作系统的减少动态效果设置解析） */
export const MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';
